import {useListKpiTargetsQuery, useListKpiTasksQuery} from "../apis/kpiApi";
import {useListPriorityPlannersQuery, useListPriorityTasksQuery} from "../apis/priorityApi";
import {useMemo} from "react";
import {useUserProfileQuery} from "../apis/authenticationApi";
import _ from "lodash";
import {DateTime} from "luxon";
import {useListSAMMeetingsQuery, useListSAMTasksQuery} from "../apis/samApi";
import {useListDepartmentsQuery} from "../apis/departmentApi";
import {useListCompanyAccountsQuery} from "../apis/companiesApi";
import statuses from 'data/task-question-status';

export const calculateStatus = (task) => {
    if(task.taskStatus === 'ToDo') {
        return task.accepted < 1000 ? "Not Accepted" : "Not Started";
    }
    else if(task.taskStatus === 'Doing') {
        return task.completionValue + "%";
    }
    else {
        return "Completed";
    }
}

export const taskStatusClass = (task) => {
    if(task.deadline === '-') return 'status-overdue';
    const deadlineDiff = DateTime.fromMillis(task.deadline).diffNow();
    const totalDiff = DateTime.fromMillis(task.deadline).diff(DateTime.fromMillis(task.created));
    let calculatedPercent = Math.min(Math.max((totalDiff - deadlineDiff) / totalDiff, 0), 1) * 100;

    if(task.created > task.deadline)
    {
        calculatedPercent = 100;
    }

    return (task.completionValue + 10) < calculatedPercent ? 'status-overdue' :
        (task.completionValue - 10) > calculatedPercent ? 'status-great' : 'status-ok';
}

const calculateTaskTreeChildren = (parent, tasks, showAll) => {
    return tasks.filter(task => (showAll || (task.taskStatus !== "Done" && task.taskStatus !== "Resolved")) && task.parentTask === parent)
        .map(task => {
            return {
                key: task.id,
                data: task,
                completed: task.taskStatus === "Done" || task.taskStatus === "Resolved",
                children: calculateTaskTreeChildren(task.id, tasks, showAll)
            }
        });
}

export const useTaskTree = (showAll, predefineTasks) => {
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: userProfile} = useUserProfileQuery();
    const {data: sams} = useListSAMTasksQuery();

    const  currentUserTaskRoots = () => {
        const userTaskHierarchy = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])]
        .filter(task => task.parentTask || (task.responsible && task.responsible === userProfile?.company?.account));

        const findUpperOwnedTask = (task, userTaskHierarchy) => {
            const parentTaskDetail = userTaskHierarchy.find(t => t.id === task.parentTask);
            if(!parentTaskDetail) {
                return false;
            } else if(parentTaskDetail.responsible === userProfile?.company?.account) {
                return true;
            } else {
                return findUpperOwnedTask(parentTaskDetail, userTaskHierarchy);
            }
        }

        return userTaskHierarchy.map((aTask)  => {
            if(!aTask.parentTask) {
                return aTask;
            } else if(aTask.responsible === userProfile?.company?.account && !findUpperOwnedTask(aTask, userTaskHierarchy)){
                return {...aTask, parentTask: undefined}; //this converts tasks without parent in root tasks
            } else return aTask;
        }) ;

    }

    return useMemo(() => {
        const userTasksAndAllSubtasks = predefineTasks || currentUserTaskRoots();

        return userTasksAndAllSubtasks
            .filter(task => (showAll || (task.taskStatus !== "Done" && task.taskStatus !== "Resolved")) && !task.parentTask)
            .map(task => {
                return {
                    key: task.id,
                    data: task,
                    completed: task.taskStatus === "Done" || task.taskStatus === "Resolved",
                    children: calculateTaskTreeChildren(task.id, userTasksAndAllSubtasks, showAll)
                }
            });
    }, [showAll, kpiTasks, priorityTasks, userProfile, sams, predefineTasks]);
}




export const useMyMessageList = ({showOpenedOnly, showDirectReporteeOnly, isOutbox, deadlineYear}) => {
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: userProfile} = useUserProfileQuery();
    const {data: samTasks} = useListSAMTasksQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();

    const userId = userProfile?.company?.account;
    const tasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(samTasks || [])];
    const hierarchyUsers = useResponsibleHiearchicalList(userProfile?.company?.account, {excludeSameUserId: true});
    const myReportees = useResponsibleReportees();
    const hierarchyUserIds = (userId && isOutbox)? [userId] : (showDirectReporteeOnly ? myReportees : (hierarchyUsers || []).map(e => e.id));
    

    return useMemo(() => {
        const finalStatusList = statuses.filter(e => e.complete).map(e => e.code);
        return tasks.filter(task => hierarchyUserIds.includes(task.responsible))
                .flatMap(task => (task.needHelpQuestionList || []).map((e, idx) => (
                {
                    ...e,
                    questionBy: companyUsers?.find(e => e.id === task.responsible),
                    task
                }
        ))).filter(e => (!showOpenedOnly || !finalStatusList.includes(e.status)) && (!deadlineYear || validateDeadlineYear(e.deadline, deadlineYear)))
           .sort((a, b) => b.createdAt - a.createdAt);
    }, [kpiTasks, priorityTasks, userProfile, samTasks, companyUsers, showOpenedOnly, showDirectReporteeOnly, myReportees, isOutbox, deadlineYear]);
}


export const findTaskIdFromTaskTree = (taskTreeStructArr, taskId) => {
    if(taskTreeStructArr.length) {
        let foundTask = (taskTreeStructArr || []).find(e => e?.key === taskId);
        if(!foundTask) {
           foundTask = findTaskIdFromTaskTree(taskTreeStructArr.flatMap(m => m?.children), taskId);
        }
        return foundTask;

    }
    return undefined;
}

export const useSubTaskTree = (task) => {
    const taskTree = useTaskTree(true);
    return useMemo(() => {
       return findTaskIdFromTaskTree(taskTree, task?.id);
    }, [taskTree, task]);
}

const fillTask = (task, allTasks, kpiTargets, priorities, samMeetings) => {
    const clonedTask = _.cloneDeep(task);

    if(clonedTask.parentTask !== '') {
        clonedTask.parentName = (allTasks || []).find(task => task.id === clonedTask.parentTask)?.name;
    }
    else if (clonedTask.taskType === 'KPI') {
        clonedTask.parentName = (kpiTargets || []).find(target => target.id === clonedTask.parent)?.name;
    } else if (task.taskType === 'Priority') {
        clonedTask.parentName = (priorities || []).find(priority => priority.id === clonedTask.parent)?.name;
    } else if (task.taskType === 'SAM') {
        clonedTask.parentName = (samMeetings || []).find(meeting => meeting.id === clonedTask.parent)?.meetingTimestamp;

        if (clonedTask.parentName) {
            clonedTask.parentName = DateTime.fromMillis(clonedTask.parentName).toISODate();
        }
    }

    const deadlineDiff = DateTime.fromMillis(clonedTask.deadline).diffNow();
    const totalDiff = DateTime.fromMillis(clonedTask.deadline).diff(DateTime.fromMillis(clonedTask.created));

    clonedTask.calculatedPercent = Math.min(Math.max((totalDiff - deadlineDiff) / totalDiff, 0), 1) * 100;
    clonedTask.deadlineMillis = clonedTask.deadline;
    clonedTask.createdMillis = clonedTask.created;
    clonedTask.acceptedMillis = clonedTask.accepted;
    clonedTask.startedMillis = clonedTask.started;

    if (clonedTask.created > clonedTask.deadline)
    {
        clonedTask.calculatedPercent = 100;
    }

    if(clonedTask.deadline > 1000) {
        clonedTask.deadline = DateTime.fromMillis(clonedTask.deadline).toISODate();
    }
    else {
        clonedTask.deadline = "-";
    }

    clonedTask.created = DateTime.fromMillis(clonedTask.created).toISODate();

    if(clonedTask.accepted > 1000) {
        clonedTask.accepted = DateTime.fromMillis(clonedTask.accepted).toISODate();
    }
    else {
        clonedTask.accepted = "-";
    }

    clonedTask.completedMillis = clonedTask.completed;

    if(clonedTask.completed > 1000) {
        clonedTask.completed = DateTime.fromMillis(clonedTask.completed).toISODate();
    }
    else {
        clonedTask.completed = "-";
    }

    if(clonedTask.started > 1000) {
        clonedTask.started = DateTime.fromMillis(clonedTask.started).toISODate();
    }
    else {
        clonedTask.started = "-";
    }

    return clonedTask;
}

const validateDeadlineYear = (dateVal, deadlineYear) => {
   /* const aDate = dateVal? DateTime.fromMillis(dateVal) : undefined;
    return !deadlineYear || (Math.max(new Date().getFullYear(), aDate?.year || (new Date().getFullYear())) === deadlineYear);*/
    //we have agreed to show always the tasks, so i will do that
    return true;
}



export const useMyTasks = (status, show, deadlineYear) => {
    const {data: kpiTargets} = useListKpiTargetsQuery();
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: userProfile} = useUserProfileQuery();
    const {data: priorities} = useListPriorityPlannersQuery();
    const {data: sams} = useListSAMTasksQuery();
    const {data: samMeetings} = useListSAMMeetingsQuery();
    
    return useMemo(() => {
        const allTasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])].sort((a, b) => (b?.deadline ?? 0) - (a?.deadline ?? 0));
        const results = allTasks
            .filter(task => show && task.parent && task.responsible && task.responsible === userProfile?.company?.account && task.taskStatus === status &&
                    (!deadlineYear || validateDeadlineYear(task?.deadline, deadlineYear)))
            .map(task => fillTask(task, allTasks, kpiTargets, priorities, samMeetings));

        if(status === 'ToDo')
            results.sort((a, b) => a.deadlineMillis - b.deadlineMillis);
        else if(status === 'Done')
            results.sort((a, b) => b.completedMillis - a.completedMillis);
        else
            results.sort((a, b) => (b.calculatedPercent - b.completionValue) - (a.calculatedPercent - a.completionValue));

        return results;
    }, [show, status, kpiTasks, kpiTargets, priorityTasks, priorities, sams, samMeetings, userProfile?.company?.account, deadlineYear]);
}




const populateDepartmentHiearchy = (hierarchy, departmentsList) => {
    const subDepartments = (hierarchy || []).filter(e => e?.parent && departmentsList.map(e => e.id).includes(e?.parent));
    subDepartments.forEach(subDepartment => {
        if(!departmentsList.includes(subDepartment)) {
            departmentsList.push(subDepartment);
            populateDepartmentHiearchy(hierarchy, departmentsList);
        }
    });
}



export const useAllDepartmentsHeads = () => {
    const {data:  hierarchy} = useListDepartmentsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();

    return useMemo(() => {
     return (companyUsers || []).filter( u => u.id === (hierarchy || []).find(e  => u.id  === e?.departmentHead)?.departmentHead);
    }, [hierarchy, companyUsers]);
}

export const useResponsibleHiearchicalList = (userId, props) => {
    const {data:  hierarchy} = useListDepartmentsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();

    return useMemo(() => {

        if(!userId) {
            return companyUsers;
        }

        const departmentsHeld = (hierarchy || []).filter(e => userId && e?.departmentHead === userId);
        populateDepartmentHiearchy(hierarchy, departmentsHeld);
        const allPeopleUnderIds = (departmentsHeld || []).map(e => e?.departmentHead);
        return (companyUsers || []).filter(e => (!props?.excludeSameUserId || e?.id !== userId) && allPeopleUnderIds.includes(e?.id));
    }, [hierarchy, companyUsers, userId]);
}


export const useResponsibleReportees = () => {
    const {data:  hierarchy} = useListDepartmentsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();
    const {data: userProfile} = useUserProfileQuery();

    return useMemo(() => {
        const userId = userProfile?.company?.account;
        if(!userId) {
            return [];
        }

        const departmentsHeld = (hierarchy || []).filter(e => userId && e?.departmentHead === userId);
        const reportees = (hierarchy || []).filter(e => e?.parent && departmentsHeld.map(e => e.id).includes(e?.parent)).map(e => e?.departmentHead);
        
        return reportees;
    }, [hierarchy, companyUsers, userProfile]);
}


export const useResponsibleManagers = (props) => {
    const {data:  hierarchy} = useListDepartmentsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();
    const {data: userProfile} = useUserProfileQuery();
    const userId = props?.userId || userProfile?.company?.account;

    return useMemo(() => {
        if(!userId) {
            return [];
        }

        const departmentsHeld = (hierarchy || []).filter(e => userId && e?.departmentHead === userId);
        const parentDepartments = (departmentsHeld || []).flatMap(d => d.parent? hierarchy.filter(e => e.id === d.parent) : []);
        
        return parentDepartments.map(d => (companyUsers || []).find( u => u.id === d.departmentHead));
    }, [hierarchy, companyUsers, userProfile]);
}

export const useParentTaskOwner = (task, acceptLoggedInUserAsParent) => {
    const {data: kpiTargets} = useListKpiTargetsQuery();
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: priorities} = useListPriorityPlannersQuery();
    const {data: sams} = useListSAMTasksQuery();
    const {data: samMeetings} = useListSAMMeetingsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();
    const {data: userProfile} = useUserProfileQuery();

    return useMemo(() => {
        if(!task.id || (!task.parent && !task.parentTask)) {
            return undefined;
        }
        const allTasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])];
        let owner = undefined;
        if(task?.parentTask) {
            owner = allTasks.find(e => e.id === task.parentTask && (acceptLoggedInUserAsParent || e.responsible !== userProfile?.company?.account))?.responsible;
        } 
        
        if(!owner) {
            owner = (kpiTargets || []).find(e => e.id === task.parent && (acceptLoggedInUserAsParent || e.responsible !== userProfile?.company?.account))?.responsible;
        }
        
        return (companyUsers || []).find( u => u.id === owner);
    }, [kpiTargets, kpiTasks, priorityTasks, priorities, sams, samMeetings, companyUsers, userProfile]);
}


export const useListDepartmentUser = (departmentId) => {
    const {data:  hierarchy} = useListDepartmentsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();

    return useMemo(() => {
        const departmentsHeld = departmentId ? (hierarchy || []).filter(e => e?.id === departmentId) : (hierarchy || []);
        return departmentsHeld.map(e => {
            const departmentList = [e];
            populateDepartmentHiearchy(hierarchy, departmentList);
            const allPeopleUnderIds = (departmentList || []).map(e => e?.departmentHead);
            return {
                ...e,
                userList: (companyUsers || []).filter(e => allPeopleUnderIds.includes(e?.id))
            }
        });

    }, [hierarchy, companyUsers]);
}




export const useCanEditTaskIds = () => {
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: sams} = useListSAMTasksQuery();
    const {data: kpiTargets} = useListKpiTargetsQuery();
    const {data: userProfile} = useUserProfileQuery();
    const hierarchyUsers = useResponsibleHiearchicalList(userProfile?.company?.account);

    return useMemo(() => {
        const allTasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])];
        let taskIds = [];

        const isMyOwnSubTask = (task, visitedTasks) => {
            if(!task || (visitedTasks || []).includes(task.id)) {
                return false;
            } else if(task?.responsible == userProfile.company.account) {
                return true;
            } else if(!task?.parentTask) {
               return false;
            } else {
                visitedTasks = visitedTasks? visitedTasks : [];
                visitedTasks.push(task.id);
                return isMyOwnSubTask(allTasks.find(t => t.id == task.parentTask), visitedTasks);
            }
        }

        const belongsToMyOwnKPI = (task) => {
            return task.taskType == "KPI" && task.parent &&
                    (kpiTargets || []).find(kpi => kpi.id == task.parent)?.responsible == userProfile?.company?.account;
        }

        if(userProfile?.company?.role === "CompanyOwner") {
            taskIds = allTasks;
        } else if(userProfile?.company?.account) {
            taskIds = allTasks.filter(task => task.responsible && hierarchyUsers?.find(
                u => u?.id === task?.responsible &&
                (task?.responsible !== userProfile.company.account
                    || (task.parentTask && isMyOwnSubTask(allTasks.find(t => t.id == task.parentTask)))
                        || (!task.parentTask && task.parent && belongsToMyOwnKPI(task)))
            ));
        }

       taskIds = taskIds.map(task => task.id);
       return taskIds;
    }, [kpiTasks, priorityTasks, sams, kpiTargets, userProfile, hierarchyUsers]);
}



export const useManageTeamTaskList = (props) => {
    const {deadlineYear} = props || {};
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: sams} = useListSAMTasksQuery();
    const {data: userProfile} = useUserProfileQuery();
    const hierarchyUsers = useResponsibleHiearchicalList(userProfile?.company?.account)?.map(e => e.id); 

    return useMemo(() => {
        const allTasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])].sort((a, b) => (b?.deadline ?? 0) - (a?.deadline ?? 0));
        return allTasks.filter(e => (hierarchyUsers || []).includes(e.responsible) && validateDeadlineYear(e?.deadline, deadlineYear));
        
    }, [kpiTasks, priorityTasks, sams, userProfile, hierarchyUsers]);
}


export const useYearTaskList = ({deadlineYear}) => {
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: sams} = useListSAMTasksQuery();
    const {data: userProfile} = useUserProfileQuery();

    return useMemo(() => {
        const allTasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])].sort((a, b) => (b?.deadline ?? 0) - (a?.deadline ?? 0));
        return allTasks.filter(e => validateDeadlineYear(e?.deadline, deadlineYear));
        
    }, [kpiTasks, priorityTasks, sams, userProfile, deadlineYear]);
}


export const useAllTasksUnder = (task, rootTasks) => {
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: sams} = useListSAMTasksQuery();
    
    return useMemo(() => {
        const allTasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])].sort((a, b) => (b?.deadline ?? 0) - (a?.deadline ?? 0));
        
        const isTaskParent = (task, taskId) => { 
            if(!taskId || !task || taskId === task.id) {
                return false;
            } else if(task.parentTask === taskId) {
                return true;
            } else  {
                return isTaskParent(task.parentTask && allTasks.find(e => e.id === task.parentTask), taskId);
            }
        }
       const filteredTasks = allTasks.filter(e => isTaskParent(e, task?.id));
       return rootTasks? filteredTasks.map(e => (e.parentTask !== task?.id? e : {...e, parentTask: undefined})) : filteredTasks;
    }, [kpiTasks, priorityTasks, sams]);
}



export const useTaskSummary = () => {
    const {data: kpiTasks} = useListKpiTasksQuery();
    const {data: priorityTasks} = useListPriorityTasksQuery();
    const {data: sams} = useListSAMTasksQuery();
    const {data:  hierarchy} = useListDepartmentsQuery();
    const {data: companyUsers} = useListCompanyAccountsQuery();
    const {data: userProfile} = useUserProfileQuery();
    const hierarchyUsers = useResponsibleHiearchicalList(userProfile?.company?.account);
    
    return useMemo(() => {
        const tasks = [...(kpiTasks || []), ...(priorityTasks || []), ...(sams || [])].sort((a, b) => (b?.deadline ?? 0) - (a?.deadline ?? 0));
        
       
        const buildDepartmentName = (department) => {
            let name = department?.name ?? "";
            const findResponsible = companyUsers?.find(u => u.id === department?.departmentHead);
            if(findResponsible?.firstName && findResponsible?.lastName) {
                name += ` (${findResponsible.firstName} ${findResponsible.lastName})` 
            }
            return name;
        }
            

       const buildDepartment = (department) => {
            const departmentTasks = (tasks || []).filter(t => t.responsible === department.departmentHead);
            const completedDepartmentTasks = departmentTasks.filter(t => t.taskStatus === "Done" || t.taskStatus === "Resolved");
            const notAcceptedDepartmentTasks = departmentTasks.filter(t => t.taskStatus === "ToDo");
            const notStartedDepartmentTasks = departmentTasks.filter(t => t.taskStatus === "Accepted");
            const startedDepartmentTasks = departmentTasks.filter(t => t.taskStatus === "Doing");
            const activeDepartmentTasks = departmentTasks.filter(t => t.taskStatus !== "Done" && t.taskStatus !== "Resolved");
            const lateTasks = activeDepartmentTasks.filter(t => t.deadline && t.deadline < new Date());

            return {
                key: department.id,
                data: { 
                    name: buildDepartmentName(department),
                    id: department.id,
                    assignedTasks: departmentTasks.length,
                    taskCompletion: completedDepartmentTasks.length,
                    taskAcceptance:notAcceptedDepartmentTasks.length,
                    taskNotStarted: notStartedDepartmentTasks.length,
                    taskStarted: startedDepartmentTasks.length,
                    activeTasks: activeDepartmentTasks.length,
                    lateTasks: lateTasks.length,
                },                    
                children: (hierarchy || []).filter(e => e.parent === department.id).map(buildDepartment)
           }
       };
       const hiearchyDepartments = ['CompanyOwner', 'CompanyAdmin'].includes(userProfile?.company?.role)?  hierarchy :
                                        (hierarchy || []).filter(e => hierarchyUsers.find(u => u.id === e?.departmentHead));
       return (hiearchyDepartments || []).filter(e => !e?.parent).map(buildDepartment);
    }, [kpiTasks, priorityTasks, sams, hierarchy, companyUsers, hierarchyUsers]);
}